import type {
  AppPipelineState,
  Deployment,
  PipelineStep,
  PlanResult,
  VerificationResult,
} from '../types.ts';

/**
 * Compute the pipeline state for all apps
 */
export const computePipelineStates = (
  apps: Array<{ name: string; environments: string }>,
  deployments: Deployment[],
  planResults: PlanResult[],
  verificationResults: VerificationResult[],
): AppPipelineState[] => {
  return apps.map((app) => {
    const enabledEnvironments = app.environments
      .split(',')
      .map((environment) => environment.trim())
      .filter(Boolean);
    const devEnabled = enabledEnvironments.includes('dev');
    const prodEnabled = enabledEnvironments.includes('prod');

    // Get the LATEST deployment for each environment
    // Deployments are ordered by created_at DESC, but there might be multiple for same version
    // Sort by createdAt to ensure we get the most recent
    const devDeployment = deployments
      .filter((d) => d.appName === app.name && d.environment === 'dev')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
    const prodDeployment = deployments
      .filter((d) => d.appName === app.name && d.environment === 'prod')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

    // Find plan results for each environment
    // Plan results are ordered by created_at DESC, so first match is the latest

    // Get the latest plan result (any version)
    const latestDevPlan = planResults.find(
      (p) => p.appName === app.name && p.environment === 'dev',
    );
    const latestProdPlan = planResults.find(
      (p) => p.appName === app.name && p.environment === 'prod',
    );

    // Get the LATEST plan result matching the deployed version (for drift detection)
    // There might be multiple plans for the same version, we want the most recent
    const devPlanForVersion = devDeployment
      ? planResults
          .filter(
            (p) =>
              p.appName === app.name &&
              p.environment === 'dev' &&
              p.version === devDeployment.version,
          )
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
      : undefined;
    const prodPlanForVersion = prodDeployment
      ? planResults
          .filter(
            (p) =>
              p.appName === app.name &&
              p.environment === 'prod' &&
              p.version === prodDeployment.version,
          )
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
      : undefined;

    // Find verification results for this deployment
    const devVerification = devDeployment
      ? verificationResults.find((v) => v.deploymentId === devDeployment.id)
      : undefined;
    const prodVerification = prodDeployment
      ? verificationResults.find((v) => v.deploymentId === prodDeployment.id)
      : undefined;

    const devDeploymentStep = computeDeploymentStep(
      devDeployment,
      devEnabled,
      latestDevPlan,
      devPlanForVersion,
    );
    const prodDeploymentStep = computeDeploymentStep(
      prodDeployment,
      prodEnabled,
      latestProdPlan,
      prodPlanForVersion,
    );

    // If dev and prod have the same version and prod has changes, mark dev as having changes too
    const sameVersion =
      devDeployment && prodDeployment && devDeployment.version === prodDeployment.version;
    const prodHasChanges = prodDeploymentStep.hasChanges;

    const finalDevDeploymentStep =
      sameVersion && prodHasChanges
        ? { ...devDeploymentStep, status: 'warning' as const, hasChanges: true }
        : devDeploymentStep;

    return {
      appName: app.name,
      devDeployment: finalDevDeploymentStep,
      devValidation: computeValidationStep(
        devDeployment,
        devEnabled,
        devVerification,
        finalDevDeploymentStep,
      ),
      prodDeployment: prodDeploymentStep,
      prodValidation: computeValidationStep(
        prodDeployment,
        prodEnabled,
        prodVerification,
        prodDeploymentStep,
      ),
    };
  });
};

/**
 * Compute the status of a deployment step
 */
const computeDeploymentStep = (
  deployment?: Deployment,
  isEnvironmentEnabled = true,
  latestPlan?: PlanResult,
  planForVersion?: PlanResult,
): PipelineStep => {
  if (!isEnvironmentEnabled) {
    return {
      status: 'disabled',
      hasChanges: false,
      validationMissing: false,
      disabledReason: 'environment_disabled',
      isOutdated: false,
      date: undefined,
    };
  }

  // No deployment exists
  if (!deployment) {
    return {
      status: 'not_deployed',
      hasChanges: latestPlan?.hasChanges ?? false,
      validationMissing: false,
      planOutput: latestPlan?.planOutput,
      isOutdated: false,
      date: undefined,
    };
  }

  // Check if deployment failed
  // GitHub deployment_status states: pending, in_progress, queued, success, failure, error, inactive
  const isFailed = deployment.status === 'failure' || deployment.status === 'error';

  if (isFailed) {
    return {
      status: 'error',
      version: deployment.version,
      hasChanges: false,
      validationMissing: false,
      planOutput: planForVersion?.planOutput,
      isOutdated: false,
      date: deployment.createdAt,
    };
  }

  // Check if deployment is currently running
  // Only in_progress or pending indicate an active deployment
  const isRunning = deployment.status === 'in_progress' || deployment.status === 'pending';

  if (isRunning) {
    return {
      status: 'pending',
      version: deployment.version,
      hasChanges: false,
      validationMissing: false,
      planOutput: planForVersion?.planOutput,
      isOutdated: false,
      date: deployment.createdAt,
    };
  }

  // Check if the deployed version has infrastructure drift
  // If no plan exists yet, assume it's clean (will be verified on next scan)
  const hasChanges = planForVersion?.hasChanges ?? false;

  // Has outstanding changes (for matching version)
  if (hasChanges) {
    return {
      status: 'warning',
      version: deployment.version,
      hasChanges: true,
      validationMissing: false,
      planOutput: planForVersion?.planOutput,
      isOutdated: false,
      date: deployment.createdAt,
    };
  }

  // All good
  return {
    status: 'success',
    version: deployment.version,
    hasChanges: false,
    validationMissing: false,
    planOutput: planForVersion?.planOutput,
    isOutdated: false,
    date: deployment.createdAt,
  };
};

/**
 * Compute the status of a validation step
 */
const computeValidationStep = (
  deployment?: Deployment,
  isEnvironmentEnabled = true,
  verification?: VerificationResult,
  deploymentStep?: PipelineStep,
): PipelineStep => {
  if (!isEnvironmentEnabled) {
    return {
      status: 'disabled',
      hasChanges: false,
      validationMissing: false,
      disabledReason: 'environment_disabled',
      isOutdated: false,
      date: undefined,
    };
  }

  // No deployment exists yet, so there is no validation to run or show.
  if (!deployment) {
    return {
      status: 'not_deployed',
      hasChanges: false,
      validationMissing: false,
      isOutdated: false,
      date: undefined,
    };
  }

  // If deployment is in progress or failed, validation should be disabled
  if (deploymentStep?.status === 'pending' || deploymentStep?.status === 'error') {
    return {
      status: 'disabled',
      version: deployment.version,
      hasChanges: false,
      validationMissing: false,
      isOutdated: false,
      date: undefined,
    };
  }

  // Validation missing (no verification result for this deployment)
  if (!verification) {
    // If deployment is stale/outdated, use disabled (light grey) for validation
    // since there's no validation to show and the deployment needs to be redeployed anyway
    if (deploymentStep?.status === 'warning') {
      return {
        status: 'disabled',
        version: deployment.version,
        hasChanges: false,
        validationMissing: true,
        isOutdated: false,
        date: undefined,
      };
    }

    // Use blue/pending since deployment exists but validation hasn't been done yet
    return {
      status: 'pending',
      version: deployment.version,
      hasChanges: false,
      validationMissing: true,
      isOutdated: false,
      date: undefined,
    };
  }

  // If deployment is outdated/stale AND there is a validation, show warning status
  if (deploymentStep?.status === 'warning') {
    return {
      status: 'warning',
      version: deployment.version,
      hasChanges: deploymentStep.hasChanges,
      validationMissing: false,
      isOutdated: deploymentStep.isOutdated,
      date: verification.createdAt,
    };
  }

  // Check verification status
  if (verification.status === 'success' || verification.status === 'passed') {
    return {
      status: 'success',
      version: deployment.version,
      hasChanges: false,
      validationMissing: false,
      isOutdated: false,
      date: verification.createdAt,
    };
  }

  if (verification.status === 'failed' || verification.status === 'error') {
    return {
      status: 'error',
      version: deployment.version,
      hasChanges: false,
      validationMissing: false,
      isOutdated: false,
      date: verification.createdAt,
    };
  }

  // Unknown status - treat as pending
  return {
    status: 'pending',
    version: deployment.version,
    hasChanges: false,
    validationMissing: true,
    isOutdated: false,
    date: verification?.createdAt,
  };
};
